/* eslint-disable no-param-reassign */
import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlagService } from '@inyova/inyova-shared';
import { Account, Customer, StaticDocument } from '@inyova/models';
import { isMinAlmostCustomer } from '@inyova/utils';

import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import * as ProfileActions from '@profile/profile.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromProfile from '@profile/profile.reducers';

import { Documents3APagination } from '@app/app.constants';
import { DocumentsActiveTab, GrowProjectDocuments, LibertyDocument, NewDocType, ProfileState } from '@shared/models/Profile';
import { SharedService } from '@shared/services/shared.service';

@Component({
  selector: 'app-profile-docs',
  templateUrl: './profile-documents.component.html',
  styleUrls: ['./profile-documents.component.scss'],
  standalone: false
})
export class ProfileDocumentsComponent implements OnInit, OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  activeTab: DocumentsActiveTab = '3b';
  appLocation = 'ch';
  currentAccount: Account;
  currentYear = new Date().getFullYear();
  customer: Customer;

  collapsedDocuments = {
    agm: false,
    egm: false,
    updates: false
  };

  error = false;
  showGeneralDocs: boolean;
  show3ADocs: boolean;
  show3BDocs = true;
  showGrowDocs = true;
  showTaxWarning3a = false;
  showTaxWarning3b = false;
  pagesSelectionOptions: number[];
  maxStep: number;
  visibleTabs: DocumentsActiveTab[];

  allDocuments$: Observable<{
    initialized: boolean;
    docsGeneral: {
      initialized: boolean;
      docs: NewDocType[];
    };
    docs3b: {
      initialized: boolean;
      taxDocs: NewDocType[];
      reportingDocs: NewDocType[];
    };
    docs3a: ProfileState['documents3A'];
    docsGrow: {
      initialized: boolean;
      projects: GrowProjectDocuments[];
    };
    docsCI: {
      initialized: boolean;
      docs: NewDocType[];
    };
    docsStatic: {
      initialized: boolean;
      agmDocs: StaticDocument[];
      egmDocs: StaticDocument[];
      CIUpdates: StaticDocument[];
      static3aDocs: StaticDocument[];
      staticGeneralDocs: StaticDocument[];
    };
  }>;

  all3ADocs: LibertyDocument[];

  selectedFilter = { code: 'all', key: 'filter' };
  showFilter = false;
  filterOptions = [
    { code: 'all', key: 'all' },
    { code: 'static', key: 'contractual' },
    { code: 'ANZCA', key: 'CA' },
    { code: 'ANZGB', key: 'CH' },
    { code: 'ANZAH', key: 'payment' },
    { code: 'PMSVM', key: 'statement' },
    { code: 'B2AB', key: 'stock' },
    { code: 'AZST3', key: 'tax' }
  ];

  titleSort = '';
  dateSort = '';
  sort = '';
  pageIndex = 1;

  constructor(
    public router: Router,
    private store: Store,
    private sharedService: SharedService,
    private ref: ElementRef,
    private featureFlagService: FeatureFlagService
  ) {
    this.store.pipe(select(fromAppRoot.selectCustomerAndCurrentAccount), take(1)).subscribe((value) => {
      if (!value.customer || !value.customer.email) {
        return;
      }

      this.customer = value.customer;
      this.maxStep = Math.max(...value.customer.accounts.map((account) => account.step_status));
      this.currentAccount = value.currentAccountDetail;

      const user3BAccount = value.customer.accounts.find((account) => account.kind === '3b');
      this.show3BDocs = user3BAccount ? user3BAccount.step_status >= 3 : false;

      const user3AAccount = value.customer.accounts.find((account) => account.kind === '3a');
      this.show3ADocs = user3AAccount ? user3AAccount.step_status >= 3 : false;

      this.showGrowDocs =
        value.customer.temporary_accounts.findIndex((account) => account.kind === 'grow' && isMinAlmostCustomer(account.step_status)) >= 0;

      this.showGeneralDocs = this.show3BDocs || this.showGrowDocs;

      const pureCI = this.customer.accounts.length === 0 && this.customer.has_crowd_investor_account;

      if (this.show3ADocs) {
        this.fetch3ADocs();
      }

      if (this.showGrowDocs) {
        this.fetchGrowDocs();
      }

      if (!this.show3BDocs && this.show3ADocs) {
        this.activeTab = '3a';
      }

      if (pureCI) {
        this.activeTab = 'CI';
      }

      this.visibleTabs = this.getVisibleTabs();
    });

    this.allDocuments$ = this.store.select(fromProfile.selectAllDocuments(this.showGeneralDocs, this.show3ADocs, this.showGrowDocs));
    this.allDocuments$.pipe(takeUntil(this.onDestroy$)).subscribe((allDocs) => {
      if (!allDocs.initialized) {
        return;
      }

      if (this.show3ADocs) {
        this.all3ADocs = allDocs.docs3a.docs;
        this.pagesSelectionOptions = this.getSelectionPages(allDocs.docs3a.pages);
        this.showTaxWarning3a = this.showIncomingTaxReportsMessage();
      }

      if (this.show3BDocs) {
        this.showTaxWarning3b = this.showIncomingTaxReportsMessage(allDocs.docs3b.taxDocs);
      }
    });
  }

  ngOnInit() {
    this.store.dispatch(ProfileActions.fetchStaticDocs());
    this.store.dispatch(ProfileActions.fetchCustomerDocs());
  }

  getVisibleTabs(): DocumentsActiveTab[] {
    const visibleTabs = [] as DocumentsActiveTab[];

    const user3AAccountStep = this.customer.accounts.find((account) => account.kind === '3a')?.step_status;
    const user3BAccountStep = this.customer.accounts.find((account) => account.kind === '3b')?.step_status;

    if (this.showGeneralDocs) {
      visibleTabs.push('general');
    }

    if (this.show3BDocs && user3BAccountStep >= 3) {
      visibleTabs.push('3b');
    }

    if (this.customer.app_location !== 'ch' && user3BAccountStep >= 3) {
      visibleTabs.push('pdf');
    }

    if (this.show3ADocs && user3AAccountStep >= 3) {
      visibleTabs.push('3a');
    }

    if (this.customer.has_crowd_investor_account) {
      visibleTabs.push('CI');
    }

    if (this.showGrowDocs) {
      visibleTabs.push('grow');
    }

    this.activeTab = visibleTabs[0];
    return visibleTabs;
  }

  getSelectionPages(pages): number[] {
    if (pages > 7) {
      return [...Array(3).keys()].concat([...Array(pages).keys()].slice(-3));
    }
    return [...Array(pages).keys()];
  }

  showIncomingTaxReportsMessage(taxDocs?: NewDocType[]): boolean {
    const customerCreatedAt = this.customer.created_at.substring(0, 4);
    const currentMonth = new Date().getMonth() + 1;
    const previousYear = (new Date().getFullYear() - 1).toString();
    const taxArrived = taxDocs ? taxDocs.some((item) => this.getTaxYear(item.attributes.title) === previousYear) : false;

    if (currentMonth === 12 || (customerCreatedAt !== this.currentYear.toString() && !taxArrived && currentMonth < 3)) {
      if (currentMonth === 12) {
        this.currentYear = new Date().getFullYear() + 1;
      }
      return true;
    }
    return false;
  }

  setActiveTab(tab: DocumentsActiveTab): void {
    if (tab === this.activeTab) {
      return;
    }

    this.activeTab = tab;
  }

  getTaxYear(backendTitle: string): string {
    if (backendTitle.length > 4) {
      return backendTitle.substring(backendTitle.length - 4);
    }
    return '';
  }

  download3ADocs(doc: LibertyDocument): void {
    if (doc.type === 'liberty_static') {
      if (typeof doc.attributes.document_external_url !== 'undefined') {
        this.sharedService.download3aStaticDocument(doc.attributes.document_external_url);
        return;
      }
      this.sharedService.downloadStaticDocument(doc.attributes.document_type, doc.id);
    } else {
      this.sharedService.downloadLibertyDocument(doc.attributes.document_type, doc.id);
    }
  }

  downloadDocument(doc: NewDocType): void {
    this.sharedService.downloadCustomerDocuments(doc.attributes.translated_title, doc.attributes.type_id, doc.id);
  }

  downloadStaticDocs(doc: StaticDocument): void {
    this.sharedService.downloadStaticDocument(doc.attributes.title, doc.id);
  }

  getFormatedDate(date: string): string {
    if (!date) {
      return '-';
    }
    const dateInitial = new Date(date);
    return dateInitial.toLocaleString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  updatePagination(currentPage: number): void {
    this.pageIndex = currentPage;
    this.fetch3ADocs();
  }

  toggleFilter(): void {
    this.showFilter = !this.showFilter;
  }

  changeSelectedFilter(option: { code: string; key: string }): void {
    this.showFilter = false;
    this.selectedFilter = option;
    this.pageIndex = 1;
    this.fetch3ADocs();
  }

  changeOrderByDate(orderby: 'document_date' | '-document_date'): void {
    if (this.dateSort === orderby) {
      this.dateSort = '';
    } else {
      this.dateSort = orderby;
    }
    this.updateSort();
    this.fetch3ADocs();
  }

  changeOrderByTitle(orderby: 'document_type' | '-document_type'): void {
    if (this.titleSort === orderby) {
      this.titleSort = '';
    } else {
      this.titleSort = orderby;
    }
    this.updateSort();
    this.fetch3ADocs();
  }

  updateSort(): void {
    if (this.dateSort && this.titleSort) {
      this.sort = `${this.titleSort},${this.dateSort}`;
    } else if (this.dateSort) {
      this.sort = `${this.dateSort}`;
    } else if (this.titleSort) {
      this.sort = `${this.titleSort}`;
    } else {
      this.sort = '';
    }
  }

  fetch3ADocs(): void {
    this.store.dispatch(
      ProfileActions.fetchLibertyDocs({
        perItem: Documents3APagination.DEFAULT_RANGE,
        page: this.pageIndex,
        sort: this.sort,
        filter: this.selectedFilter.code
      })
    );
  }

  fetchGrowDocs(): void {
    this.store.dispatch(ProfileActions.fetchGrowDocs());
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  documentClick(event) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const targetElement = event.target as HTMLElement;
    const dropdownTrigger = document.getElementById('filter-selected');
    if (targetElement && dropdownTrigger && !dropdownTrigger.contains(targetElement)) {
      this.showFilter = false;
    }
  }
}
